import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiForm, applyApiError, useApiForm } from '../../shared/form';
import { cn } from '../../shared/lib/cn';
import { Button } from '../../shared/ui/button';
import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import {
  CheckResult,
  CREDENTIAL_APPS,
  createCredential,
  credentialKeys,
  testCredential,
  type ICredentialApp,
  type TConnector,
  type TCredentialSaved,
} from '../../entities/credential';
import { AppIcon } from './app-icon';
import { CredentialFields } from './credential-fields';
import {
  credentialSchema,
  defaultValues,
  fieldsOf,
  formFields,
  type TCredentialValues,
} from './lib';

interface IFormProps {
  app: ICredentialApp;
  connectors: readonly TConnector[];
  onOpenChange: (open: boolean) => void;
  onFailed: (saved: TCredentialSaved) => void;
}

const CredentialForm = ({ app, connectors, onOpenChange, onFailed }: IFormProps) => {
  const client = useQueryClient();
  const create = useMutation({ mutationFn: createCredential });
  const check = useMutation({
    mutationFn: (fields: Record<string, unknown>) => testCredential(app.connectorKey, fields),
  });

  const form = useApiForm<TCredentialValues>({
    schema: credentialSchema(app, connectors),
    defaultValues: defaultValues(app, connectors),
    onSubmit: (values) =>
      create.mutateAsync(
        {
          connectorKey: app.connectorKey,
          name: values.name?.trim() ?? '',
          fields: fieldsOf(app, connectors, values),
        },
        {
          onSuccess: async (saved) => {
            await client.invalidateQueries({ queryKey: credentialKeys.all });
            onOpenChange(false);
            if (saved.check && !saved.check.ok) onFailed(saved);
          },
        },
      ),
  });

  // A result belongs to the fields it was run on; typing makes it a claim about other ones.
  const { watch } = form.form;
  const { reset: resetCheck } = check;
  const { clearRejection } = form;
  useEffect(() => {
    const subscription = watch(() => resetCheck());
    return () => subscription.unsubscribe();
  }, [watch, resetCheck]);

  const runCheck = async () => {
    const paths = formFields(app, connectors).map((field) => field.path);
    if (!(await form.form.trigger(paths))) return;
    check.mutate(fieldsOf(app, connectors, form.form.getValues()), {
      // A banner from an earlier Save says nothing about fields that just passed a Check.
      onSuccess: () => clearRejection(),
      onError: (error) => applyApiError(form.form, error),
    });
  };

  const saving = form.form.formState.isSubmitting;

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      width="md"
      title={app.title}
      description={app.subtitle}
      footer={
        <Button
          variant="ghost"
          size="sm"
          waiting={check.isPending}
          waitingLabel="Checking"
          onClick={() => void runCheck()}
        >
          Check
        </Button>
      }
      submit={{
        label: 'Save',
        onSubmit: () => void form.submit(),
        waiting: saving,
        waitingLabel: 'Checking and saving',
      }}
    >
      <ApiForm api={form} flush>
        <CredentialFields app={app} connectors={connectors} />
        {check.data && <CheckResult ok={check.data.ok} detail={check.data.detail} />}
        {check.isError && <MutationError error={check.error} />}
        {/* Enter in a field submits the form; without this button it would do nothing. */}
        <button type="submit" hidden />
      </ApiForm>
    </Modal>
  );
};

interface IAddCredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectors: readonly TConnector[];
  onFailed: (saved: TCredentialSaved) => void;
}

export const AddCredentialsDialog = ({
  open,
  onOpenChange,
  connectors,
  onFailed,
}: IAddCredentialsDialogProps) => {
  const [app, setApp] = useState<ICredentialApp | null>(null);

  const close = (next: boolean) => {
    if (!next) setApp(null);
    onOpenChange(next);
  };

  if (!open) return null;

  if (app) {
    return (
      <CredentialForm
        key={app.app}
        app={app}
        connectors={connectors}
        onOpenChange={close}
        onFailed={onFailed}
      />
    );
  }

  const offered = CREDENTIAL_APPS.filter((candidate) =>
    connectors.some((connector) => connector.key === candidate.connectorKey),
  );

  return (
    <Modal
      open
      onOpenChange={close}
      width="md"
      title="Add credentials"
      footer={
        <Button variant="secondary" size="sm" onClick={() => close(false)}>
          Cancel
        </Button>
      }
    >
      <div className="grid grid-cols-2 gap-2">
        {offered.map((candidate) => (
          <button
            key={candidate.app}
            type="button"
            onClick={() => setApp(candidate)}
            className={cn(
              'flex h-control items-center gap-inset rounded-control border border-transparent px-inset',
              'bg-field-muted text-left text-data text-primary',
              'hover:border-border-raised hover:bg-hover-raised',
            )}
          >
            <AppIcon app={candidate.app} />
            {candidate.choice}
          </button>
        ))}
      </div>
    </Modal>
  );
};
